import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import Snackbar from './Snackbar/Snackbar';
import { useBranding } from '../context/BrandingContext';
import { createOrder, verifyLinePayment, createLinePayment, createMercadoPagoPreference } from '../services/api';
import { useSession } from '../context/SessionContext';
import { useLiveAnnouncer } from '../context/LiveAnnouncerContext';
import { trackPixel } from '../utils/analytics';
import CustomerAuthModal from './CustomerAuthModal';
import LinePaymentModal from './LinePaymentModal';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import './CartDrawer.css';

function CartDrawer({ isOpen, onClose, embedded = false, mode = 'dine_in', onOrderPlaced }) {
  const { t } = useTranslation();
  const { cart, removeItem, restoreItem, updateQuantity, getSubtotal, getServiceCharge, getTax, getTotal, clearCart, orderConfirmed, confirmOrder } = useCart();
  const { settings, paymentMethods } = useBranding();
  const featureFlags = useFeatureFlags();
  const { session } = useSession();
  const { announce } = useLiveAnnouncer();
  const [view, setView] = useState('cart'); // 'cart', 'summary', 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentAction, setCurrentAction] = useState(null); // 'waiter', 'bill', 'payment'
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [linePaymentId, setLinePaymentId] = useState(null);
  const [linePaymentQr, setLinePaymentQr] = useState(null);
  const [linePaymentDeeplink, setLinePaymentDeeplink] = useState(null);
  const [removedItem, setRemovedItem] = useState(null);
  const [showRemoveSnackbar, setShowRemoveSnackbar] = useState(false);
  const removeSnackbarTimer = useRef(null);

  const handleRemoveItem = useCallback((item) => {
    if (removeSnackbarTimer.current) {
      clearTimeout(removeSnackbarTimer.current);
    }
    const savedItem = { ...item };
    removeItem(item.cartItemId);
    setRemovedItem(savedItem);
    setShowRemoveSnackbar(true);
    announce(t('a11y.itemRemoved', { name: item.name }));
  }, [removeItem, announce, t]);

  const handleUndoRemove = useCallback(() => {
    if (removedItem) {
      restoreItem(removedItem);
      setRemovedItem(null);
      setShowRemoveSnackbar(false);
      announce(t('a11y.itemRestored', { name: removedItem.name }));
    }
  }, [removedItem, restoreItem, announce, t]);

  const handleDismissRemoveSnackbar = useCallback(() => {
    setShowRemoveSnackbar(false);
    setRemovedItem(null);
  }, []);

  // Reset cart panel when new item is added after order confirmation
  useEffect(() => {
    if (view === 'success' && !orderConfirmed && cart.length > 0) {
      setView('cart');
      setCurrentAction(null);
      setError(null);
    }
  }, [orderConfirmed, view, cart.length]);

  // Auto-open drawer when items are added to cart (mobile behavior)
  useEffect(() => {
    if (cart.length > 0 && !isOpen) {
      // Automatically open in collapsed state when items exist
      // This will be handled by the parent component
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]);

  const handleContinueToSummary = () => {
    // Check if user has identified themselves
    if (!session?.customer) {
      setIsAuthModalOpen(true);
      return;
    }

    trackPixel('InitiateCheckout', {
      content_ids: cart.map(item => item.itemId),
      num_items: cart.reduce((sum, item) => sum + item.quantity, 0),
      value: getTotal(),
      currency: 'ARS',
    });

    setView('summary');
    setError(null);
    setIsExpanded(true); // Ensure drawer is expanded when going to summary
  };

  const handleAuthSuccess = (customerData) => {
    setIsAuthModalOpen(false);
    // After auth, continue to summary
    setView('summary');
    setIsExpanded(true);
  };

  const handleBackToCart = () => {
    setView('cart');
    setError(null);
  };

  const handleAction = async (actionType) => {
    setLoading(true);
    setError(null);
    setCurrentAction(actionType);

    try {
      const customerInfo = session?.customer?.isGuest === false
        ? {
            name: session.customer.name,
            email: session.customer.email,
            ...(session.customer.phone && { phone: session.customer.phone }),
            dataConsent: true,
          }
        : {
            name: session?.customer?.name || 'Guest',
            dataConsent: true,
          };

      const orderType = mode === 'takeaway' ? 'takeaway' : mode === 'delivery' ? 'delivery' : 'dine_in';

      const orderData = {
        restaurantId: session?.restaurantId,
        sessionId: session?.sessionId,
        orderType,
        tableNumber: session?.tableNumber,
        customerName: session?.customer?.name || 'Guest',
        customerId: session?.customer?.customerId,
        customerInfo,
        ...(actionType === 'payment' && { awaitingPayment: true }),
        ...(mode === 'delivery' && session?.deliveryAddress && {
          deliveryAddress: session.deliveryAddress,
          deliveryFee: session.deliveryAddress.deliveryFee || 0,
        }),
        items: cart.map(item => ({
          itemId: item.itemId,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          unitPrice: item.unitPrice,
          selectedModifiers: item.selectedModifiers || [],
          notes: item.notes || ''
        }))
      };

      console.log('Creating order with data:', orderData);
      const response = await createOrder(orderData);
      const orderId = response.orderId || response.order?.orderId;

      // Notify parent that an order was placed (activates pending bill polling)
      if (onOrderPlaced) onOrderPlaced();

      // If payment option, create payment and open modal
      if (actionType === 'payment') {
        setCreatedOrderId(orderId);

        try {
          if (paymentMethods?.mercadoPago?.enabled) {
            // MercadoPago Checkout Pro flow
            const mpResponse = await createMercadoPagoPreference({
              sessionId: session?.sessionId,
              splitMode: 'pay_all',
              payerName: session?.customer?.name || 'Guest',
              payerId: session?.customer?.customerId,
            });
            if (mpResponse.checkoutUrl) {
              window.location.href = mpResponse.checkoutUrl;
            } else {
              throw new Error(mpResponse.message || 'Failed to create MercadoPago preference');
            }
          } else {
            // Line Checkout fallback
            const paymentResponse = await createLinePayment(orderId);
            if (paymentResponse.success && paymentResponse.paymentId) {
              setLinePaymentId(paymentResponse.paymentId);
              setLinePaymentQr(paymentResponse.qr || null);
              setLinePaymentDeeplink(paymentResponse.deeplink || null);
              setIsPaymentModalOpen(true);
            } else {
              throw new Error(paymentResponse.message || 'Failed to create payment');
            }
          }
        } catch (paymentErr) {
          console.error('Error creating payment:', paymentErr);
          setError(t('payment.createError', 'Error al iniciar el pago'));
          return;
        }
      } else {
        // For waiter or bill request, show success immediately
        confirmOrder();
        setView('success');
      }
      // Don't clear cart yet - do it when closing the success view or after payment
    } catch (err) {
      console.error('Order error:', err);
      setError(t('cart.checkout_error') || 'Error al procesar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentResponse) => {
    console.log('Payment successful:', paymentResponse);
    setIsPaymentModalOpen(false);
    confirmOrder();
    setView('success');
  };

  const handleVerifyPayment = async ({ paymentId, orderId, amount }) => {
    try {
      console.log('Verifying payment:', { paymentId, orderId, amount });
      const result = await verifyLinePayment(paymentId, orderId, amount);
      return result.success;
    } catch (error) {
      console.error('Payment verification failed:', error);
      return false;
    }
  };

  const handleClose = () => {
    if (view === 'success') {
      clearCart();
    }
    setView('cart');
    setCurrentAction(null);
    setError(null);
    setIsExpanded(false);
    onClose();
  };

  const toggleExpanded = () => {
    if (cart.length > 0 || view === 'success') {
      setIsExpanded(!isExpanded);
    }
  };

  // Render functions for reuse between embedded and drawer modes
  const renderCartView = () => (
    <div className="cart-expanded">
      <div className="cart-header">
        <h2>{t('cart.title') || 'Tu Pedido'}</h2>
        {!embedded && (
          <button className="cart-close" onClick={handleClose}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        )}
      </div>

      <div className="cart-items-list">
        {cart.length === 0 ? (
          <div className="cart-empty">
            <span className="material-icons" aria-hidden="true">shopping_cart</span>
            <p>{t('cart.empty') || 'Tu carrito está vacío'}</p>
          </div>
        ) : (
          cart.map(item => (
            <div key={item.cartItemId} className="cart-item">
              <div className="cart-item-info">
                <div className="cart-item-name">{item.name}</div>
                {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                  <div className="cart-item-modifiers">
                    {item.selectedModifiers.map((mod, idx) => (
                      <span key={idx} className="cart-item-modifier">
                        {mod.optionName}
                      </span>
                    ))}
                  </div>
                )}
                {item.notes && (
                  <div className="cart-item-notes">
                    <em>{item.notes}</em>
                  </div>
                )}
                <div className="cart-item-price">${(item.price * item.quantity).toFixed(2)}</div>
              </div>
              <div className="cart-item-quantity">
                <button
                  type="button"
                  className="qty-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (item.quantity === 1) {
                      handleRemoveItem(item);
                    } else {
                      updateQuantity(item.cartItemId, item.quantity - 1);
                      announce(t('a11y.quantityUpdated', { name: item.name, quantity: item.quantity - 1 }));
                    }
                  }}
                >
                  <span className="material-icons" aria-hidden="true">{item.quantity === 1 ? 'delete' : 'remove'}</span>
                </button>
                <span className="qty-value">{item.quantity}</span>
                <button
                  type="button"
                  className="qty-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    updateQuantity(item.cartItemId, item.quantity + 1);
                    announce(t('a11y.quantityUpdated', { name: item.name, quantity: item.quantity + 1 }));
                  }}
                >
                  <span className="material-icons" aria-hidden="true">add</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {cart.length > 0 && (
        <div className="cart-footer">
          <div className="cart-total">
            <div className="total-breakdown">
              <div className="total-line">
                <span>{t('cart.subtotal')}:</span>
                <span>${getSubtotal().toFixed(2)}</span>
              </div>
              {getServiceCharge(settings) > 0 && (
                <div className="total-line">
                  <span>{t('cart.serviceCharge')} ({settings?.serviceCharge ?? 10}%):</span>
                  <span>${getServiceCharge(settings).toFixed(2)}</span>
                </div>
              )}
              {getTax(settings) > 0 && (
                <div className="total-line">
                  <span>{t('cart.tax')} ({settings?.taxRate ?? 21}%):</span>
                  <span>${getTax(settings).toFixed(2)}</span>
                </div>
              )}
              <div className="total-line total-final">
                <strong>{t('cart.total')}:</strong>
                <strong className="total-amount">${getTotal(settings).toFixed(2)}</strong>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="checkout-btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleContinueToSummary();
            }}
          >
            {t('cart.continue') || 'Continuar'}
          </button>
        </div>
      )}
    </div>
  );

  const renderSummaryView = () => (
    <div className="cart-expanded">
      <div className="cart-header">
        <h2>{t('cart.summary_title') || 'Resumen del Pedido'}</h2>
        {!embedded && (
          <button className="cart-close" onClick={handleClose}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        )}
      </div>

      <div className="summary-view">
        {mode === 'delivery' && session?.deliveryAddress && (
          <div className="delivery-address-summary">
            <span className="material-icons" aria-hidden="true">location_on</span>
            <span>{t('deliveryAddress.deliveryTo', { address: session.deliveryAddress.street })}</span>
          </div>
        )}
        <div className="summary-header">
          <span className="material-icons" aria-hidden="true">receipt_long</span>
          <h3>{t('cart.order_details') || 'Detalles del pedido'}</h3>
        </div>

        <div className="summary-items">
          {cart.map(item => (
            <div key={item.cartItemId} className="summary-item">
              <div className="summary-item-main">
                <span className="summary-item-qty">{item.quantity}x</span>
                <div className="summary-item-details">
                  <span className="summary-item-name">{item.name}</span>
                  {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                    <div className="summary-item-modifiers">
                      {item.selectedModifiers.map((mod, idx) => (
                        <span key={idx} className="summary-item-modifier">
                          {mod.optionName}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.notes && (
                    <div className="summary-item-notes">
                      <em>{item.notes}</em>
                    </div>
                  )}
                </div>
              </div>
              <span className="summary-item-price">${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="summary-totals">
          <div className="total-line">
            <span>{t('cart.subtotal')}:</span>
            <span>${getSubtotal().toFixed(2)}</span>
          </div>
          {getServiceCharge(settings) > 0 && (
            <div className="total-line">
              <span>{t('cart.serviceCharge')} ({settings?.serviceCharge ?? 10}%):</span>
              <span>${getServiceCharge(settings).toFixed(2)}</span>
            </div>
          )}
          {getTax(settings) > 0 && (
            <div className="total-line">
              <span>{t('cart.tax')} ({settings?.taxRate ?? 21}%):</span>
              <span>${getTax(settings).toFixed(2)}</span>
            </div>
          )}
          {mode === 'delivery' && session?.deliveryAddress?.deliveryFee > 0 && (
            <div className="total-line">
              <span>{t('cart.deliveryFee')}:</span>
              <span>${Number(session.deliveryAddress.deliveryFee).toFixed(2)}</span>
            </div>
          )}
          <div className="total-line total-final">
            <strong>{t('cart.total')}:</strong>
            <span className="amount">
              ${(getTotal(settings) + (mode === 'delivery' ? Number(session?.deliveryAddress?.deliveryFee || 0) : 0)).toFixed(2)}
            </span>
          </div>
        </div>

        {error && <div className="checkout-error" role="alert">{error}</div>}

        <div className="summary-footer">
          {mode === 'dine_in' && (
            <button
              type="button"
              className="action-btn order-only-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAction('order_only');
              }}
              disabled={loading}
            >
              <span className="material-icons" aria-hidden="true">restaurant</span>
              <div className="action-text">
                <strong>{t('cart.order_only') || 'Pedir'}</strong>
                <small>{t('cart.order_only_desc') || 'Pagar al final de la visita'}</small>
              </div>
            </button>
          )}
          {featureFlags.paymentIntegrationsEnabled && (
          <button
            type="button"
            className="action-btn payment-now-btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleAction('payment');
            }}
            disabled={loading}
          >
            <span className="material-icons" aria-hidden="true">payment</span>
            <div className="action-text">
              <strong>{t('cart.pay_now') || 'Pagar Ahora'}</strong>
              <small>{t('cart.pay_now_desc')}</small>
            </div>
          </button>
          )}
          <button
            type="button"
            className="back-link"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleBackToCart();
            }}
          >
            <span className="material-icons" aria-hidden="true">arrow_back</span>
            {t('common.back') || 'Volver'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderSuccessView = () => (
    <div className="cart-expanded">
      <div className="cart-header">
        <h2>{t('cart.success_title') || '¡Listo!'}</h2>
        {!embedded && (
          <button className="cart-close" onClick={handleClose}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        )}
      </div>

      <div className="success-view">
        <div className="success-icon">
          <span className="material-icons" aria-hidden="true">check_circle</span>
        </div>
        <h2>
          {currentAction === 'payment'
            ? (t('cart.payment_success') || '¡Pago Exitoso!')
            : currentAction === 'order_only'
            ? (t('cart.order_placed') || '¡Pedido Enviado!')
            : currentAction === 'waiter'
            ? (t('cart.waiter_called') || '¡Mozo Llamado!')
            : (t('cart.bill_requested') || '¡Cuenta Solicitada!')}
        </h2>
        <p className="success-message">
          {currentAction === 'payment'
            ? (t('cart.payment_complete') || 'Tu pago ha sido procesado correctamente.')
            : currentAction === 'order_only'
            ? (t('cart.order_placed_desc') || 'Tu pedido fue enviado a la cocina.')
            : currentAction === 'waiter'
            ? (t('cart.waiter_success') || 'El mozo recibirá tu pedido en breve.')
            : (t('cart.bill_success') || 'El mozo traerá tu cuenta en breve.')}
        </p>
        <button
          type="button"
          className="success-btn"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (embedded) {
              setView('cart');
              setCurrentAction(null);
              clearCart();
            } else {
              handleClose();
            }
          }}
        >
          <span className="material-icons" aria-hidden="true">check</span>
          {t('common.continue') || 'Continuar'}
        </button>
      </div>
    </div>
  );

  // Show drawer if it's open OR if there are items in cart OR if showing success view
  const shouldShowDrawer = isOpen || (cart.length > 0) || view === 'success';

  // In embedded mode, always show (even if empty)
  if (!embedded && !shouldShowDrawer) return null;

  // Determine if drawer should be in expanded state
  const drawerExpanded = embedded || isExpanded || view === 'summary' || view === 'success';

  // Embedded mode - render without overlay, fills container
  if (embedded) {
    return (
      <div className="cart-drawer cart-drawer--embedded">
        {/* Always show expanded cart view in embedded mode */}
        {view === 'cart' && renderCartView()}
        {view === 'summary' && renderSummaryView()}
        {view === 'success' && renderSuccessView()}

        {/* Customer Authentication Modal */}
        <CustomerAuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          onSuccess={handleAuthSuccess}
          restaurantId={session?.restaurantId}
          organizationId={session?.organizationId}
        />

        {/* Line Payment Modal */}
        <LinePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            setLinePaymentId(null);
            setLinePaymentQr(null);
            setLinePaymentDeeplink(null);
          }}
          paymentId={linePaymentId}
          amount={getTotal(settings)}
          orderId={createdOrderId}
          qr={linePaymentQr}
          deeplink={linePaymentDeeplink}
          onSuccess={handlePaymentSuccess}
          onVerifyPayment={handleVerifyPayment}
        />

        <Snackbar
          message={t('cart.itemRemoved', { name: removedItem?.name })}
          isVisible={showRemoveSnackbar}
          onDismiss={handleDismissRemoveSnackbar}
          onUndo={handleUndoRemove}
          duration={5000}
        />
      </div>
    );
  }

  // Mobile/overlay mode
  return (
    <>
      {drawerExpanded && <div className="cart-drawer-backdrop" onClick={handleClose} />}
      <div className={`cart-drawer ${drawerExpanded ? 'expanded' : 'collapsed'}`}>

        {/* Handle bar for dragging */}
        <div className="cart-drawer-handle" onClick={toggleExpanded}>
          <div className="handle-bar"></div>
        </div>

        {/* Collapsed Mini Bar - Only show when not expanded and in cart view */}
        {!drawerExpanded && view === 'cart' && cart.length > 0 && (
          <div className="cart-mini-bar" onClick={toggleExpanded}>
            <div className="cart-mini-info">
              <div className="cart-mini-count">
                {cart.length} {cart.length === 1 ? t('cart.item') : t('cart.items')}
              </div>
              <div className="cart-mini-total">${getTotal(settings).toFixed(2)}</div>
            </div>
            <button className="cart-mini-checkout" onClick={(e) => {
              e.stopPropagation();
              handleContinueToSummary();
            }}>
              {t('cart.view_order') || 'Ver pedido'}
              <span className="material-icons" aria-hidden="true">arrow_upward</span>
            </button>
          </div>
        )}

        {/* Use shared render functions */}
        {drawerExpanded && view === 'cart' && renderCartView()}
        {view === 'summary' && renderSummaryView()}
        {view === 'success' && renderSuccessView()}
      </div>

      {/* Customer Authentication Modal */}
      <CustomerAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
        restaurantId={session?.restaurantId}
        organizationId={session?.organizationId}
      />

      {/* Line Payment Modal */}
      <LinePaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setLinePaymentId(null);
          setLinePaymentQr(null);
          setLinePaymentDeeplink(null);
        }}
        paymentId={linePaymentId}
        amount={getTotal(settings)}
        orderId={createdOrderId}
        qr={linePaymentQr}
        deeplink={linePaymentDeeplink}
        onSuccess={handlePaymentSuccess}
        onVerifyPayment={handleVerifyPayment}
      />

      <Snackbar
        message={t('cart.itemRemoved', { name: removedItem?.name })}
        isVisible={showRemoveSnackbar}
        onDismiss={handleDismissRemoveSnackbar}
        onUndo={handleUndoRemove}
        duration={5000}
      />
    </>
  );
}

export default CartDrawer;
